"use client";

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { authService } from "@/services/auth.service";

export default function PrivacyConsentModal({ open, onClose, onAgree, onDisagree, onTermsComplete, userIdx }: { open: boolean; onClose: () => void; onAgree: () => void; onDisagree?: () => void; onTermsComplete?: () => void; userIdx?: string }) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAgree = async () => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      // props로 전달된 userIdx 사용, 없으면 sessionStorage에서 가져오기
      const pendingUserIdx = userIdx || (typeof window !== 'undefined' ? window.sessionStorage.getItem('pendingUserCode') : null);
      
      // console.log('[PrivacyConsentModal] Using userIdx:', pendingUserIdx, 'from:', userIdx ? 'props' : 'sessionStorage');

      if (!pendingUserIdx) {
        console.error('[PrivacyConsentModal] Pending user idx not found');
        onClose();
        return;
      }

      // 정보동의 API 호출
      const data = await authService.privacyConsent({ 
        agreed: true
      });

      if (data.code === "SUCCESS") {
        // activate 플로우인 경우 sessionStorage에 플래그 설정
        if (pendingUserIdx) {
          sessionStorage.setItem('activateFlow', 'privacy_completed');
        }
        
        // 정보동의 완료 후 처리는 부모 컴포넌트에서 진행
        onAgree();
      } else {
        console.error('Privacy consent failed:', data.details?.reason);
        onClose();
      }
    } catch (error) {
      console.error('Privacy consent error:', error);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    if (open) window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div data-layer="Frame 1707482037" className="relative w-[540px] h-[497px]">
        <div data-layer="Rectangle 343611" className="w-[540px] h-[497px] left-0 top-0 absolute bg-white rounded-2xl shadow-[0px_0px_18px_0px_rgba(162,166,173,0.20)]" />

        <button aria-label="닫기" data-layer="close" onClick={onDisagree ?? onClose} className="size-7 left-[488px] top-[24px] absolute text-[#65676e] hover:text-[#010103] transition-colors">
          <X className="size-7" />
        </button>

        <div data-layer="[필수] 개인정보 수집·이용 동의" className="left-[32px] top-[56px] absolute text-black text-3xl font-semibold leading-10" style={{ fontFamily: 'var(--font-pretendard)' }}>
          [필수] 개인정보 수집·이용 동의1111
        </div>

        <div data-layer="Frame 1707482007" className="w-[476px] left-[32px] top-[119px] absolute inline-flex flex-col justify-start items-start gap-5">
          <div data-layer="Frame 1707482006" className="self-stretch flex flex-col justify-start items-start gap-2">
            <div data-layer="Frame 1707482003" className="inline-flex justify-start items-center gap-3">
              <div data-layer="수집 항목" className="text-gray-900 text-base font-medium leading-normal" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>수집 항목</div>
              <div data-layer="이름, 회사명, 이메일 주소, 휴대폰 번호" className="text-gray-700 text-base font-normal leading-normal" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>이름, 회사명, 이메일 주소, 휴대폰 번호</div>
            </div>
            <div data-layer="Frame 1707482004" className="inline-flex justify-start items-center gap-3">
              <div data-layer="수집·이용 목적" className="text-gray-900 text-base font-medium leading-normal" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>수집·이용 목적</div>
              <div data-layer="회원가입 및 서비스 제공, 고객문의 응대, 공지사항 전달" className="text-gray-700 text-base font-normal leading-normal" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>회원가입 및 서비스 제공, 고객문의 응대, 공지사항 전달</div>
            </div>
            <div data-layer="Frame 1707482005" className="self-stretch inline-flex justify-start items-start gap-3">
              <div data-layer="보유·이용 기간" className="text-gray-900 text-base font-medium leading-normal" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>보유·이용 기간</div>
              <div data-layer="회원 탈퇴 시까지(단, 관련 법령에 따라 보관이 필요한 경우 해당 기간 보관)" className="w-96 text-gray-700 text-base font-normal leading-normal" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>
                회원 탈퇴 시까지(단, 관련 법령에 따라 보관이 필요한 경우 해당 기간 보관)
              </div>
            </div>
          </div>
          <div data-layer="귀하는 개인정보 수집·이용에 동의하지 않을 권리가 있으며, 다만 동의하지 않을 경우 회원가입이 제한될 수 있습니다." className="self-stretch text-gray-700 text-base font-normal leading-normal" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>
            귀하는 개인정보 수집·이용에 동의하지 않을 권리가 있으며, 다만 동의하지 않을 경우 회원가입이 제한될 수 있습니다.
          </div>
        </div>

        <div className="absolute left-0 right-0 top-[347px] flex items-center justify-center gap-3">
          <button data-layer="Frame 1707481736" onClick={onDisagree ?? onClose} className="w-64 h-16 px-6 py-3 bg-zinc-200 rounded-lg inline-flex justify-center items-center gap-2.5 text-neutral-500 text-lg font-medium hover:bg-zinc-300 transition-colors" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>
            미동의
          </button>
          <button 
            data-layer="Frame 2860" 
            onClick={handleAgree} 
            disabled={isSubmitting}
            className="w-64 h-16 px-6 py-3 bg-black rounded-lg inline-flex justify-center items-center gap-2.5 text-white text-lg font-semibold hover:bg-[#1a1a1a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors" 
            style={{ fontFamily: 'var(--font-pretendard)' }}
          >
            {isSubmitting ? "처리 중..." : "동의"}
          </button>
        </div>

        <a href="#" data-layer="회원정보 삭제" className="left-[225px] top-[443px] absolute text-center text-zinc-500 text-base font-normal underline hover:text-[#010103] transition-colors" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>
          회원정보 삭제
        </a>
      </div>
    </div>
  );
}


